import { AfterContentInit, Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { FormComponentLookup, FormEntryComponentClass } from "@ptl/forms";
import { Subscription, Observable, merge, map } from "rxjs";
import { DateOfLifeEventCustomAttributes, LifeEventCustomAttributes, ApplicableCircumstanceCustomAttributes } from "./interfaces/special-enrollment-qualification-page-proxy.interface";
import { clearAllProxyValues, setProxyValue } from "../../plan-enrollment/individual-plan-enrollment-form/configs/reducers/special-enrollment-circumstance.reducer";
import { submittedAttemptProxyConnectPath } from "src/app/angular-elements/shared/interfaces/special-enrollment-circumstances.interface";

/**
 * Nik 7/7/2026 - This component is pretty clunky in its implementation due to the face we do not have a good way to handle proxy values at this time
 * We handle the date value by storing a temporary cookie vs the redux store to store temporary values for the other form fields on the qualifying event pages
 * This form differs from the multi-page form because we initialize all the forms / form-fields within one component in order to detect their interaction with each other.
 * 
 * However, it does become bulky pulling out the forms into their own smaller components in order to modify their validation behavior within the parent form.
 * In the future, this component should be rewritten with a better way to hold the proxy components (ie: a more typical forms as json structure where each form field -
 * exists separately in it's own entry in the json) 
 * 
 * Whether we pull out the cookie storing of the temp date and move it to the redux form structure like we see here with the event value or applicable circumstance value,
 * OR we move those two values to the temp cookie storage. Maybe in the future there will be a more elegant way to streamline this process and store these values prior to 
 * navigating away from the page instead of the conglomerate of cruft we see here. 
 * 
 * Updating the forms ptl piece to inherently create proxy values to components added to the form and running default validation logic to only store them on submit -
 * would be the ultimate solution to solve our proxy issue. 
 */


@FormComponentLookup('special-enrollment-single-page-proxy')

@Component({
  selector: "app-special-enrollment-single-page-proxy",
  templateUrl: "./special-enrollment-single-page-proxy.component.html",
  styleUrl: "./special-enrollment-single-page-proxy.component.scss",
  standalone: false
})
export class SpecialEnrollmentSinglePageProxyComponent extends FormEntryComponentClass implements OnInit, AfterContentInit {
  constructor(
    private fb: FormBuilder
  ) {
    super();
  }

  radioForm: FormGroup;
  eventForm: FormGroup;
  dateForm: FormGroup;
  proxyFormsSubscription: Subscription;

  submitAttemptedObservable$: Observable<boolean>;
  submitAttemptedSubscription: Subscription;

  showNextQuestion: boolean;
  lifeEventSelectorAttributes: LifeEventCustomAttributes;
  dateOfLifeEventSelectorAttributes: DateOfLifeEventCustomAttributes;
  applicableCircumstanceRadioAttributes: ApplicableCircumstanceCustomAttributes;

  ngOnInit(){
    super.ngOnInit();
    this.setUpStoreVariables();

    this.clearProxyValues();

    /* Proxy Form Initialization */

    //TODO - NIK: Add commments for ViewProviders

    this.radioForm = this.fb.group<any>({
      [this.applicableCircumstanceRadioAttributes.formControlAttributes.formControlName]: this.fb.control<string>(null, {
        validators: [Validators.required]
      })
    });

    this.eventForm = this.fb.group<any>({
      [this.lifeEventSelectorAttributes.formControlAttributes.formControlName]: this.fb.control<string>(null, {validators: [Validators.required]})
    })

    this.dateForm = this.fb.group<any>({
      [this.dateOfLifeEventSelectorAttributes.formControlAttributes.formControlName]: this.fb.control<string>(null, {
        validators: [Validators.required]
      }),
    })

    /** Combined all proxy form subscriptions into one large subscription for succinctness, readability, and to reflect DRY principles
     * we map the formName to the values in order to clearly differentiate which form is being updated to cut down on unnecessary calls of other forms -- 
     * when only one form is updated.
     */


    this.proxyFormsSubscription = merge(      
      this.radioForm.valueChanges.pipe(map(changedValue => ({ formName: 'radioForm', value: changedValue }))),
      this.eventForm.valueChanges.pipe(map(changedValue => ({ formName: 'eventForm', value: changedValue }))),
      this.dateForm.valueChanges.pipe(map(changedValue => ({ formName: 'dateForm', value: changedValue })))
      )
      .subscribe((changedForm: any) => {
      switch(changedForm.formName){
        case("eventForm"):
          if(this.eventForm.valid){
            this.ngRedux.dispatch(setProxyValue(this.currentPage?.custom?.proxyLifeEventConnectPath, changedForm.value.lifeEvent));
            
            //if a user has selected a date before they selected the event, run the error logic to check if the pre-chosen date actually matches the chosen event
            if(this.dateForm.dirty){
              this.dateForm.controls[this.dateOfLifeEventSelectorAttributes.formControlAttributes.formControlName].updateValueAndValidity();
            }
          }
          break;
        case("dateForm"):
          if(this.dateForm.valid){
            const date = changedForm.value.dateOfLifeEvent;
            this.ngRedux.dispatch(setProxyValue(this.currentPage?.custom?.proxyDateOfLifeEventConnectPath, date));
          }
          break;
        case("radioForm"):
          if(this.radioForm.valid){
            this.showNextQuestion = changedForm.value.isApplicableCircumstance;
            this.ngRedux.dispatch(setProxyValue(this.currentPage.custom.proxyApplicableCircumstanceConnectPath, changedForm.value.isApplicableCircumstance));
            //manual display of the submitButton due to not being able to access the "isApplicableCircumstance" proxy control values from the forms
            this.formConfig.form.formNavigation.submitButton.display = changedForm.value.isApplicableCircumstance === "No" ? false : true;
          }
          break;
      }
      if(this.eventForm.valid && this.dateForm.valid && this.radioForm.valid){
        this.forceParentFormValidity(true);
      }else{
        this.forceParentFormValidity(false);
      }

    });

    //if a user attempts to submit the form, mark all of them as touched to trigger error detection on non-touched form fields
    this.submitAttemptedObservable$ = this.ngRedux.select(['payloadStates', 'special-enrollment-circumstance', submittedAttemptProxyConnectPath]);
    this.submitAttemptedSubscription = this.submitAttemptedObservable$.subscribe((submitAttempted: boolean) => {
      if(submitAttempted){
        this.markAllProxyFormsAsTouched();

        if(this.radioForm.errors || this.eventForm.errors || this.dateForm.errors){
          this.forceParentFormValidity(false);
        }
      }
    })
  }

  ngAfterContentInit(): void {
    // Initially ensure the form itself is invalid so that we cannot navigate until the proxy behavior has completed  
    // Timeout to get out of the forms-as-json initial validation
    setTimeout(() => {
      this.forceParentFormValidity(false);
    }, 0)
  }

  ngOnDestroy(): void {
    if(this.proxyFormsSubscription){
      this.proxyFormsSubscription.unsubscribe();
    }
  }

  setUpStoreVariables(){

    if(this.hasFormConfigError()){
      throw new Error('Form Input Config Error!');
    }

    //store attribute paths to make code easier to read throughout
    this.lifeEventSelectorAttributes = this.entry.custom.lifeEventSelectorAttributes;
    this.dateOfLifeEventSelectorAttributes = this.entry.custom.dateOfLifeEventSelectorAttributes;
    this.applicableCircumstanceRadioAttributes = this.entry.custom.applicableCircumstanceRadioAttributes;

    //store paths in order to access in child forms
    this.applicableCircumstanceRadioAttributes.proxyApplicableCircumstanceConnectPath = this.currentPage.custom.proxyApplicableCircumstanceConnectPath;
    this.lifeEventSelectorAttributes.proxyLifeEventConnectPath = this.currentPage.custom.proxyLifeEventConnectPath;
    this.dateOfLifeEventSelectorAttributes.proxyDateOfLifeEventConnectPath = this.currentPage.custom.proxyDateOfLifeEventConnectPath;
    this.dateOfLifeEventSelectorAttributes.proxyLifeEventConnectPath = this.currentPage.custom.proxyLifeEventConnectPath;
  }

  markAllProxyFormsAsTouched(){
    this.eventForm.controls[this.lifeEventSelectorAttributes.formControlAttributes.formControlName].markAsTouched();
    this.eventForm.controls[this.lifeEventSelectorAttributes.formControlAttributes.formControlName].updateValueAndValidity();

    this.dateForm.controls[this.dateOfLifeEventSelectorAttributes.formControlAttributes.formControlName].markAsTouched();
    this.dateForm.controls[this.dateOfLifeEventSelectorAttributes.formControlAttributes.formControlName].updateValueAndValidity();

    this.radioForm.controls[this.applicableCircumstanceRadioAttributes.formControlAttributes.formControlName].markAsTouched();
    this.radioForm.controls[this.applicableCircumstanceRadioAttributes.formControlAttributes.formControlName].updateValueAndValidity();
  }

  clearProxyValues(){
    const proxyValuePathArray: string[] = [this.currentPage.custom.proxyLifeEventConnectPath, this.currentPage.custom.proxyApplicableCircumstanceConnectPath, this.currentPage.custom.proxyDateOfLifeEventConnectPath, submittedAttemptProxyConnectPath];
    this.ngRedux.dispatch(clearAllProxyValues(proxyValuePathArray));
  }

  //if any of the child forms are invalid, force the form that controls the submission process to be invalid as well 
   forceParentFormValidity(shouldForceValid: boolean) {
    if (!shouldForceValid) {
      this.form.controls[this.entry.formControlAttributes.formControlName].setErrors({ 'forced_invalid': true });
    } else {
      const currentErrors = this.form.controls[this.entry.formControlAttributes.formControlName].errors;
      if (currentErrors) {
        delete currentErrors['forced_invalid'];
      }
      this.form.controls[this.entry.formControlAttributes.formControlName].setErrors(currentErrors);
      this.form.controls[this.entry.formControlAttributes.formControlName].updateValueAndValidity();
    }
  }

  hasFormConfigError(){
    if(!this.currentPage?.custom?.proxyApplicableCircumstanceConnectPath ||
      !this.currentPage?.custom?.proxyLifeEventConnectPath ||
      !this.currentPage?.custom?.proxyDateOfLifeEventConnectPath || 
      !this.entry?.formControlAttributes?.connectPath ||
      !this.entry?.custom?.lifeEventSelectorAttributes?.formControlAttributes?.formControlName || 
      !this.entry?.custom?.dateOfLifeEventSelectorAttributes?.formControlAttributes?.formControlName || 
      !this.entry?.custom?.applicableCircumstanceRadioAttributes?.formControlAttributes?.formControlName ||
      !this.entry?.custom?.applicableCircumstanceRadioAttributes?.radioGroupAttributes?.optionAttributes ||
      !this.entry?.custom?.lifeEventSelectorAttributes?.custom?.qualifyingLifeEvents){
      return true;
    }
    return false;
  }
}
